import { useEffect } from "react";

const standalone = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.matchMedia("(display-mode: minimal-ui)").matches ||
  ("standalone" in navigator &&
    (navigator as { standalone?: boolean }).standalone === true);

/**
 * Prevents the PWA from being closed by the edge-swipe back gesture.
 *
 * Strategy:
 *  1. After React Router has initialised (and set history.state.idx), push
 *     TWO sentinel entries. Android can consume one entry before our popstate
 *     handler fires, so two buffers ensures at least one always remains.
 *  2. On every popstate, if we landed on a non-React-Router state (our
 *     sentinel, or something before it) OR on React Router's root entry
 *     (idx === 0), push two fresh sentinels so the *next* back gesture hits
 *     the buffer rather than closing the app.
 *  3. For idx > 0 (normal in-app back navigation) we do nothing so React
 *     Router's own history management is not disturbed.
 *  4. A passive:false touchstart listener blocks left-edge swipes at the
 *     touch level before Android's OS gesture navigation can claim them.
 *
 * Why not in main.tsx?
 *   React Router v6 calls history.replaceState() when it first renders,
 *   overwriting whatever we push before createRoot(). The guard must run
 *   inside the React tree (useEffect) so it fires *after* React Router's
 *   initialisation.
 */
export function usePWABackGuard() {
  useEffect(() => {
    if (!standalone()) return;

    // Push two sentinels above React Router's base entry (idx=0).
    // Android may consume one before our handler fires — the second is the real buffer.
    history.pushState({ _moriGuard: true }, "");
    history.pushState({ _moriGuard: true }, "");

    const onPopState = (e: PopStateEvent) => {
      const s = e.state as { idx?: number } | null;

      if (!s || s.idx === undefined) {
        // Popped to our sentinel (or pre-router state) — re-push two to keep the buffer.
        history.pushState({ _moriGuard: true }, "");
        history.pushState({ _moriGuard: true }, "");
        return;
      }

      if (s.idx === 0) {
        // Popped all the way to React Router's root entry — push two sentinels above it.
        history.pushState({ _moriGuard: true }, "");
        history.pushState({ _moriGuard: true }, "");
      }
      // idx > 0: normal in-app back — don't touch history.
    };

    // Block left-edge swipes on Android before the OS gesture navigation system
    // claims them. The left 20px is the Android back-gesture trigger zone.
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches[0].clientX <= 20) {
        e.preventDefault();
      }
    };

    window.addEventListener("popstate", onPopState);
    document.addEventListener("touchstart", onTouchStart, { passive: false });

    return () => {
      window.removeEventListener("popstate", onPopState);
      document.removeEventListener("touchstart", onTouchStart);
    };
  }, []);
}
